import {
  AsyncOperationProgress,
  AsyncOperationProgressAbsoluteWrapper,
  OperationProgressItem,
  useAsyncOperationProgress,
} from "@/components/progress";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import useQueryData from "@/hooks/useQueryData";
import { useFormAutoReset } from "@/hooks/useFormAutoReset";
import { FilterType } from "@/lib/filters";
import { formatDate } from "@/lib/utils/formatters";
import { capitalize } from "@/lib/utils";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { upsertProjectLimit } from "@/schemas/projectLimit";
import { UpsertProjectLimit } from "@/types/projectLimit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useProjectLimitsContext } from "./ProjectLimitsContext";
import ProjectLimitUpsertForm from "./ProjectLimitUpsertForm";
import { AsyncOperationItem } from "@/components/progress/AsyncOperationItem";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { displayProjectStore } from "@/schemas/projectStore";
import { DisplayProjectStore } from "@/types/projectStore";
import Form from "@/components/Form";
import { Button } from "@/components/ui/button";
import StoreGroupFilter from "../StoreGroupFilter";
import { XCircle } from "lucide-react";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";
import { ProjectLimitUpsertSaveButton } from "./ProjectLimitUpsertSaveButton";
import { useProjectLimitUpsertMutation } from "./hooks/useProjectLimitUpsertMutation";

interface ProjectLimitUpsertDialogProps {
  selectedMonthYear: Date;
}

const StoresValidationError = ({ hasError }: { hasError: boolean }) => {
  const { t } = useTranslation();
  const selectionCount = useRowSelectionCount();

  if (!hasError || selectionCount > 0) return null;

  return (
    <span className="flex items-center gap-2 shrink-0 text-destructive">
      <XCircle className="w-4 h-4 shrink-0" />
      {t("projectLimits.periodsDialog.errors.stores")}
    </span>
  );
};

const ProjectLimitUpsertDialogContent = ({
  selectedMonthYear,
}: ProjectLimitUpsertDialogProps) => {
  const { isAddLimitsDialogOpen, setIsAddLimitsDialogOpen } =
    useProjectLimitsContext();
  const { store } = useRowSelectionContext();
  const { t } = useTranslation();
  const name = "projectStores";
  const projectId = useProjectId();
  const [storesError, setStoresError] = useState(false);

  // Store form values for rendering completed items
  const formValuesRef = useRef<{
    entityName: string;
    hours: string;
    rate: string;
    months: number;
    stores: number;
  } | null>(null);

  const progress = useAsyncOperationProgress();
  const { query, options } = useProjectStoresQuery({
    projectId,
    enabled: isAddLimitsDialogOpen,
    filters: [
      {
        id: "project_id",
        value: {
          value: projectId,
          filter: FilterType.IsNotEmpty,
        },
      },
    ],
  });

  const tableData = useQueryData(query);

  const defaultValues = useMemo<UpsertProjectLimit>(
    () => ({
      from: formatDate(selectedMonthYear, DEFAULT_DATE_FORMAT),
      to: formatDate(selectedMonthYear, DEFAULT_DATE_FORMAT),
      entityId: "",
      entityName: "",
      hours: "0",
      rate: "0",
      addition: "0",
      period: formatDate(selectedMonthYear, DEFAULT_DATE_FORMAT),
    }),
    [selectedMonthYear],
  );

  const form = useForm<UpsertProjectLimit>({
    resolver: zodResolver(upsertProjectLimit),
    defaultValues,
  });

  useFormAutoReset({
    form,
    defaultValues,
    shouldReset: isAddLimitsDialogOpen,
    hasData: tableData.length > 0,
  });

  const getSelectedStoreIds = useCallback(
    () => Array.from(store.getSelection()),
    [store],
  );

  const projectLimitMutation = useProjectLimitUpsertMutation({
    projectId,
    progress,
    form,
    formValuesRef,
    getSelectedStoreIds,
  });

  const handleSubmit = useCallback(
    async ({ entityName, ...values }: UpsertProjectLimit) => {
      const selectedIds = Array.from(store.getSelection());
      if (selectedIds.length === 0) {
        setStoresError(true);
        return;
      }
      setStoresError(false);
      await projectLimitMutation.mutateAsync(values);
    },
    [projectLimitMutation, store],
  );

  const handleProgressReset = useCallback(() => {
    form.reset(defaultValues);
    store.clear();
  }, [defaultValues, form, store]);

  const handleProgressClose = useCallback(() => {
    setIsAddLimitsDialogOpen(false);
  }, [setIsAddLimitsDialogOpen]);

  useEffect(() => {
    // Reset the progress and selection when the dialog is closed
    if (!isAddLimitsDialogOpen) {
      progress.reset();
      store.clear();
      setStoresError(false);
    }
  }, [progress, isAddLimitsDialogOpen, store]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Only allow closing if we're not in PROCESSING or CANCELLING state
      if (!open && progress.isBusy()) {
        return;
      }
      setIsAddLimitsDialogOpen(open);
    },
    [progress, setIsAddLimitsDialogOpen],
  );

  const renderCompletedItem = useCallback(
    (item: OperationProgressItem) => {
      const formValues = formValuesRef.current;
      const entityName = formValues?.entityName || "";
      const description = `${t("projectLimits.periodsDialog.role")}: ${entityName || ""}, ${t("projectLimits.periodsDialog.hours")}: ${formValues?.hours || "0"}, ${t("projectLimits.periodsDialog.rate")}: ${formValues?.rate || "0"}`;

      // Capitalize first letter of month name
      const capitalizedMonth = capitalize(item.label);

      return (
        <AsyncOperationItem
          label={capitalizedMonth}
          description={description}
          success={item.success}
        />
      );
    },
    [t],
  );

  return (
    <Dialog open={isAddLimitsDialogOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90dvw] h-[95dvh]">
        <AsyncOperationProgressAbsoluteWrapper status={progress.status}>
          <AsyncOperationProgress
            operationProgress={progress}
            onReset={handleProgressReset}
            onClose={handleProgressClose}
            translationKey="projectLimits.periodsDialog"
            renderCompletedItem={renderCompletedItem}
            i18nMetadata={{
              months: formValuesRef.current?.months ?? 0,
              stores: formValuesRef.current?.stores ?? 0,
            }}
          />
        </AsyncOperationProgressAbsoluteWrapper>
        <Form form={form} handleSubmit={handleSubmit} className="min-w-0">
          <DialogHeader>
            <DialogTitle>{t("projectLimits.periodsDialog.title")}</DialogTitle>
          </DialogHeader>
          <DataTableQueryContextProvider value={{ query }}>
            <QueryTable<DisplayProjectStore>
              schema={displayProjectStore}
              name={name}
              query={query}
              options={options}
              tableFeatures={{
                enableRowSelection: true,
                enableSelectAll: true,
              }}
            >
              <div className="flex flex-row items-center gap-2">
                <QueryTable.Menubar className="m-0 grow">
                  <QueryTable.Menubar.Tools inDialog />
                  <QueryTable.Menubar.Columns inDialog />
                  <QueryTable.Menubar.Reset inDialog />
                </QueryTable.Menubar>
                <StoreGroupFilter inDialog className="grow-0 shrink-0" />
              </div>
              <div className="flex flex-row grow gap-2 overflow-hidden">
                <QueryTable.Content
                  className="overflow-x-auto flex-grow"
                  isInDialog
                >
                  <QueryTable.Status>
                    <QueryTable.SelectedRowsStatus />
                    <StoresValidationError hasError={storesError} />
                  </QueryTable.Status>
                </QueryTable.Content>
                <ProjectLimitUpsertForm />
              </div>
            </QueryTable>
          </DataTableQueryContextProvider>
          <DialogFooter className="gap-3 ">
            <DialogClose asChild>
              <Button variant="link">
                {t("projectLimits.periodsDialog.cancel")}
              </Button>
            </DialogClose>
            <ProjectLimitUpsertSaveButton />
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const ProjectLimitUpsertDialog = ({
  selectedMonthYear,
}: ProjectLimitUpsertDialogProps) => {
  return (
    <RowSelectionProvider>
      <ProjectLimitUpsertDialogContent selectedMonthYear={selectedMonthYear} />
    </RowSelectionProvider>
  );
};

export default ProjectLimitUpsertDialog;
